import { HF_CONFIG, getCanonicalSymbol } from '../config/constants'
import { getOrWaitForAuthToken } from '../services/firebase'
import ky from 'ky'

// In-memory cache for monthly file directories
const monthlyHFCache = new Map<string, string[]>()

const HF_REPO_URL = HF_CONFIG?.BASE_API_URL || 'https://huggingface.co/api/datasets/bathinimallikarjuna637/Options-Oracle/tree/main'

/**
 * Fetches all available .parquet files for a specific Symbol, Year, and Month.
 * Returns an array of available string dates in format 'DD' or 'DD.parquet',
 * allowing the UI to instantly pinpoint which days are green/red.
 * 
 * Includes aggressive local cache to avoid rate-limits while browsing calendar.
 */
export async function fetchHFAvailableDates(symbol: string, year: number, month: number): Promise<Set<number>> {
    // Zero-pad the month (e.g. 04)
    const mm = month.toString().padStart(2, '0')
    const yyyy = year.toString()
    
    // Safety fallback: if canonical symbol mapping needed, add here.
    const canonicalSymbol = (getCanonicalSymbol(symbol) || symbol) as string

    const cacheKey = `${canonicalSymbol}-${yyyy}-${mm}`
    
    // Check Cache First
    if (monthlyHFCache.has(cacheKey)) {
        return new Set(monthlyHFCache.get(cacheKey)?.map(Number) || [])
    }

    try {
        const url = `/api/hf-tree-proxy?symbol=${canonicalSymbol}&year=${yyyy}&month=${mm}`
        
        const token = await getOrWaitForAuthToken()
        const response = await fetch(url, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined
        })

        if (!response.ok) {
            if (response.status === 404) {
                monthlyHFCache.set(cacheKey, [])
                return new Set()
            }
            throw new Error(`HTTP ${response.status}`)
        }

        const data: Array<{ path: string, type: string }> = await response.json()
        
        // Extract the day integers from the filenames (e.g. '12.parquet' -> 12)
        const days = data
            .filter(item => item.type === 'file' && item.path.endsWith('.parquet'))
            .map(item => {
                const parts = item.path.split('/')
                const filename = parts[parts.length - 1]
                return filename ? parseInt(filename.split('.')[0] || '', 10) : NaN
            })
            .filter(day => !isNaN(day))
        
        monthlyHFCache.set(cacheKey, days.map(String))
        
        return new Set(days)
    } catch (err: any) {
        console.warn('[HF Utils] Could not fetch tree for', cacheKey, err.message)
        return new Set()
    }
}
